import {
  TEST_ID,
  BYTES,
  ADD_VARIABLE,
  DELETE_VARIABLE,
  ROLE_PERMISSIONS,
  USER_TYPE_SYSTEM,
} from "./constants";
import t from "./en";
import { getPermissionsSnapshot } from "../contexts/permissionsCache";

export const addAnswerContainer = (form, param) => {
  let copy = [...form?.[param]];
  copy.unshift({ id: `TEST_ID${copy.length + 1}` });

  return { ...form, [param]: [...copy] };
};

export const removeAnswerContainer = (form, idx, param) => {
  let copy = [...(form?.[param] ?? [])];

  if (copy?.length > 1) {
    copy.splice(idx, 1);
  } else {
    copy[idx] = { id: TEST_ID }; // To make react-beautiful-dnd work,
    // each element needs an id, so I set the id to dummy new_answer.
  }

  copy = copy.map((it, itIdx) => ({
    ...it,
    ...(!it?.id?.includes(TEST_ID)
      ? { id: it.id }
      : { id: `${TEST_ID}${itIdx + 1}` }),
  }));

  return { ...form, [param]: [...copy] };
};

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const dragEnd = (form, result, param) => {
  // dropped outside the list
  if (!result.destination) {
    return;
  }

  const items = reorder(
    form?.[param],
    result.source.index,
    result.destination.index
  );

  return { ...form, [param]: items };
};

export const changeTxt = (evt, form) => {
  const { name, value } = evt.target;

  return { ...form, [name]: value };
};

export const changeSubTxt = (evt, form, idx, param) => {
  const { name, value } = evt.target;
  let copy = [...form?.[param]];

  copy[idx] = { ...copy?.[idx], [name]: value };
  return { ...form, [param]: copy };
};

export const selectOption = (_, value, param, form) => {
  return { ...form, [param]: value };
};

export const selectMultipleSubOption = (_, value, name, idx, form, param) => {
  if (value !== null) {
    let copy = [...form?.[param]];

    copy[idx] = { ...copy?.[idx], [name]: value };
    return { ...form, [param]: copy };
  }
};

export const filterRecords = (
  result,
  currentFilters,
  filterByParams,
  shouldReturnValueOnly
) => {
  if (!currentFilters.searchedValue) {
    return shouldReturnValueOnly
      ? result
      : (prevProps) => ({ ...prevProps, filtered: result });
  }

  const searchValue = currentFilters.searchedValue.toLowerCase().trim();

  const copyFilteredData = result.filter((item) => {
    const params = filterByParams(item);

    return params.some((val) => val?.toLowerCase().includes(searchValue));
  });

  if (shouldReturnValueOnly) {
    return copyFilteredData;
  } else {
    return (prevProps) => ({ ...prevProps, filtered: copyFilteredData });
  }
};

export const string2Array = (value) => {
  return value
    ?.split(/[\n,]+/)
    .map((it) => it.trim())
    .filter((it) => it.length > 0);
};

export const convert2MB = (val) => {
  if (!!+val) {
    return parseFloat(
      (
        val / Math.pow(BYTES, Math.floor(Math.log(val) / Math.log(BYTES)))
      ).toFixed(1)
    );
  } else {
    return 0;
  }
};

export const setConfigsAndSecrets = (evt, elem) => {
  const { id } = evt.target;
  const { form, item, param } = elem;
  let cpy = {
    ...form,
    variables: [...form.variables],
    secrets: [...form.secrets],
  };

  if (id === ADD_VARIABLE) {
    cpy?.[param].unshift({});
  } else if (id === DELETE_VARIABLE) {
    cpy?.[param].splice(item?.currentItemIdx, 1);
  }

  return cpy;
};

export const configsAndSecretsShowPwd = async (e, elem) => {
  const { form, getSecret, showError, item } = elem;
  let cpy = { ...form };

  if (item?.value?.length > 0) {
    try {
      const result = await new Promise((resolve, reject) => {
        getSecret({ name: cpy.name, key: item.key }, (result, err) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });

      cpy[item.paramName][item.valIdx] = {
        ...cpy[item.paramName][item.valIdx],
        value: result,
        isPwdVisible: true,
      };
    } catch (error) {
      showError(error);
    }
  } else {
    cpy[item.paramName][item.valIdx].isPwdVisible = true;
  }

  return cpy;
};

export const setJsonVariable2Table = (elem) => {
  let cpy = { ...elem };

  try {
    const parsed = JSON.parse(cpy?.secondModal?.value);
    const parsed2KeyValue = Object.entries(parsed)?.map(([key, value]) => {
      return { key, value };
    });

    cpy[cpy?.tabVal === 0 ? "variables" : "secrets"] = parsed2KeyValue;
    cpy.secondModal = null;

    return cpy;
  } catch {
    return elem;
  }
};

export const onValidateField = (form, errors, fieldName) => {
  const updatedErrors = { ...errors };

  switch (fieldName) {
    case "displayName":
      if (!form.displayName) {
        updatedErrors.displayName = t.required;
      } else {
        delete updatedErrors.displayName;
      }
      break;

    case "git_repo":
      if (!form.git_repo) {
        updatedErrors.git_repo = t.required;
      } else {
        delete updatedErrors.git_repo;
      }
      break;

    case "git_revision_selector":
      if (!form.git_revision_selector) {
        updatedErrors.git_revision_selector = t.required;
      } else {
        delete updatedErrors.git_revision_selector;
      }
      break;

    case "git_secrets":
      if (form.isGitSecretsRequired && !form.git_secrets) {
        updatedErrors.git_secrets = t.required;
      } else {
        delete updatedErrors.git_secrets;
      }
      break;

    case "git_cred_username":
      if (form.isGitCredUsernameRequired && !form.git_cred_username) {
        updatedErrors.git_cred_username = t.required;
      } else {
        delete updatedErrors.git_cred_username;
      }
      break;

    case "git_cred_password":
      if (form.isGitCredPasswordRequired && !form.git_cred_password) {
        updatedErrors.git_cred_password = t.required;
      } else {
        delete updatedErrors.git_cred_password;
      }
      break;

    case "registry_secrets":
      if (form.isRegistrySecretsRequired && !form.registry_secrets) {
        updatedErrors.git_secrets = t.required;
      } else {
        delete updatedErrors.registry_secrets;
      }
      break;

    case "registry_cred_username":
      if (form.isRegistryCredUsernameRequired && !form.registry_cred_username) {
        updatedErrors.registry_cred_username = t.required;
      } else {
        delete updatedErrors.registry_cred_username;
      }
      break;

    case "registry_cred_password":
      if (form.isRegistryCredPasswordRequired && !form.registry_cred_password) {
        updatedErrors.registry_cred_password = t.required;
      } else {
        delete updatedErrors.registry_cred_password;
      }
      break;

    default:
      break;
  }

  return updatedErrors;
};

export const onValidateFormStep = (form, step) => {
  const errors = {};

  switch (step) {
    case 0: 
      if (!form.displayName) {
        errors.displayName = t.required;
      }
      break;

    case 1:
      if (!form?.git_repo) {
        errors.git_repo = t.required;
      }

      if (!form?.git_revision) {
        errors.git_revision = t.required;
      }

      if (form.isGitSecretsRequired && !form.git_secrets) {
        errors.git_secrets = t.required;
      }

      if (form.isGitCredUsernameRequired && !form.git_cred_username) {
        errors.git_cred_username = t.required;
      }

      if (form.isGitCredPasswordRequired && !form.git_cred_password) {
        errors.git_cred_password = t.required;
      }
      break;

    case 2:
      if (form.isRegistrySecretsRequired && !form.registry_secrets) {
        errors.registry_secrets = t.required;
      }

      if (form.isRegistryCredUsernameRequired && !form.registry_cred_username) {
        errors.registry_cred_username = t.required;
      }

      if (form.isRegistryCredPasswordRequired && !form.registry_cred_password) {
        errors.registry_cred_password = t.required;
      }
      break;

    case 3:
      if (form.deployAutomatically && !form.container_port) {
        errors.container_port = t.required;
      }

      if (form.scaleTo0 && !form.up_time) {
        errors.up_time = t.required;
      }

      if (form.scaleTo0 && !form.down_time) {
        errors.down_time = t.required;
      }

      if (form.scaleTo0 && !form.timezone) {
        errors.timezone = t.required;
      }

      break;

    default:
      return {}
  }

  return errors;
}

export const onValidateForm = (form) => {
  const errors = {};

  if (!form.displayName) {
    errors.displayName = t.required;
  }
  if (!form.git_repo) {
    errors.git_repo = t.required;
  }
  if (!form.git_revision_selector) {
    errors.git_revision_selector = t.required;
  }
  if (form.isGitSecretsRequired && !form.git_secrets) {
    errors.git_secrets = t.required;
  }
  if (form.isGitCredUsernameRequired && !form.git_cred_username) {
    errors.git_cred_username = t.required;
  }
  if (form.isGitCredPasswordRequired && !form.git_cred_password) {
    errors.git_cred_password = t.required;
  }
  if (form.isRegistrySecretsRequired && !form.registry_secrets) {
    errors.registry_secrets = t.required;
  }
  if (form.isRegistryCredUsernameRequired && !form.registry_cred_username) {
    errors.registry_cred_username = t.required;
  }
  if (form.isRegistryCredPasswordRequired && !form.registry_cred_password) {
    errors.registry_cred_password = t.required;
  }

  return errors;
};

/**
 * Returns true if the caller holds ANY of the given entitlement keys.
 *
 * Entitlement keys are dot-notation strings such as
 * `directory.user.manage`, `directory.group.manage`, `member.view`.
 * They come from `GET /api/me/permissions` — the SCK-547 emitter
 * which, post-PR-1.5b, includes dot-notation keys derived from
 * `role_permissions ⋈ role_assignments` at every scope the caller has
 * an active assignment on.
 *
 * SYSTEM is a wildcard — matches every entitlement key, same
 * short-circuit that `hasPermission` uses. The check honours both the
 * legacy `currentUser.userType === "SYSTEM"` JWT claim and a "SYSTEM"
 * verb present in the permissions snapshot (the reactive path via
 * <PermissionsProvider>).
 *
 * SIGNATURE
 * ---------
 *   `hasEntitlement(currentUser, keys)` — mirrors `hasPermission`.
 *   Reads `currentUser.effectivePermissions` first (explicit prop
 *   pass-through, useful for tests and for surfaces that already
 *   attach the entitlement list to the JWT-derived user object) then
 *   falls back to the module-level snapshot populated by
 *   `<PermissionsProvider>` on mount / focus / 60s poll — same
 *   backing cache as `hasPermission`.
 *
 * PREFER
 * ------
 *   Entitlement-based gates in NAV_ITEMS and other cross-tenant
 *   surfaces (e.g. `directory.user.manage`) over role-name gates
 *   (e.g. `SYSTEM_DIRECTORY_ADMIN`) — role names are being renamed
 *   scope-by-scope by PR-1.5f and downstream; entitlement keys are
 *   stable across those renames.
 */
export const hasEntitlement = (currentUser, keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return false;

  // JWT-claim wildcard for platform super-users.
  if (currentUser?.userType === USER_TYPE_SYSTEM) return true;

  // Explicit prop pass-through takes precedence over the snapshot so
  // callers (and unit tests) can supply an entitlement list without
  // touching module state.
  const snap = getPermissionsSnapshot();
  const perms =
    (Array.isArray(currentUser?.effectivePermissions) &&
      currentUser.effectivePermissions) ||
    snap?.permissions ||
    [];

  // SYSTEM in the snapshot behaves as a wildcard too — same as
  // `hasPermission` — so a snapshot-derived super-user matches every
  // requested key.
  if (perms.includes("SYSTEM")) return true;

  return keys.some((k) => perms.includes(k));
};

// Check if user has any of the required permissions.
//
// SCK-547: this function used to derive gates purely from JWT claims
// captured at login, which meant role changes on the server never
// reached the client until sign-out + sign-in. It now consults the
// module-level cache populated by <PermissionsProvider> first — that
// cache is refreshed on mount, route change, window focus, and every
// 60s — and only falls back to the legacy JWT/ROLE_PERMISSIONS
// mapping when the provider hasn't produced a snapshot yet or the
// coord `/api/me/permissions` endpoint isn't deployed (source =
// 'jwt' after a 404 fallback).
//
// The old signature `hasPermission(currentUser, [perm, ...])` is
// intentionally preserved so 30+ call-sites don't churn in one PR.
// The `currentUser` argument is now only used for the legacy
// fallback path. Prefer `useHasPermission(perm)` in new code — it
// subscribes to context and re-renders on refetch.
//
// The follow-up plan (per SCK-547 body) is to migrate every call-site
// to the hook and delete ROLE_PERMISSIONS entirely.
export const hasPermission = (currentUser, permissions) => {
  const snap = getPermissionsSnapshot();

  if (snap.source === "api") {
    // SYSTEM is a synthetic verb the provider adds when the caller is
    // a platform-super-user; it matches everything, same as the
    // legacy check on userType === SYSTEM.
    if (snap.permissions.includes("SYSTEM")) return true;
    return permissions.some((p) => snap.permissions.includes(p));
  }

  // ---- Legacy JWT-decode fallback (unchanged behavior) --------------
  if (currentUser?.userType === USER_TYPE_SYSTEM) {
    return true;
  } else if (currentUser?.groups) {
    for (let role of currentUser.groups) {
      // Check if any of the required permissions is in the role's permission list
      if (
        ROLE_PERMISSIONS[role?.userRole.roleType] &&
        permissions.some((permission) =>
          ROLE_PERMISSIONS[role?.userRole.roleType].includes(permission)
        )
      ) {
        return true; // User has at least one of the permissions
      }
    }
  }

  return false; // User does not have any of the permissions
};
